export const UNSPLASH = (id) => `https://images.unsplash.com/${id}?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80`;
export const PEXELS = (id) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=900`;
export const seeded = (slug, i = 1) => `https://picsum.photos/seed/printzee-${slug}-${i}/900/900`;

export const normalize = (s) => {
  let res = s.toLowerCase()
    .replace(/meter/g, "mtr")
    .replace(/and/g, "&")
    .replace(/\s*\(.*\)/g, ""); // Strip parenthetical descriptions like (White)
  
  // Mirror the exact variant suffix-stripping used during asset loading:
  res = res.replace(/[-_ ]?\d+$/, "").replace(/\s*\(?\d+\)?$/, "");
  
  // Also handle camera/alphanumeric code suffix: e.g. abih7627 -> abih, img0327 -> img
  const strippedAlphaNum = res.replace(/[^a-z0-9]/g, "");
  if (/^[a-z]{4}\d+$/.test(strippedAlphaNum) || /^img\d+$/.test(strippedAlphaNum)) {
    res = res.replace(/\d+$/, "");
  }
  
  return res.replace(/[^a-z0-9]/g, "");
};

export const colors = [
  { bg: "0F172A", text: "F59E0B" }, // Luxury Navy with Gold text
  { bg: "2563EB", text: "FFFFFF" }, // Royal Blue with White text
  { bg: "F59E0B", text: "0F172A" }, // Gold with Navy text
  { bg: "475569", text: "F1F5F9" }, // Slate with Light Slate text
  { bg: "0284C7", text: "FFFFFF" }, // Sky Blue with White text
  { bg: "4F46E5", text: "FFFFFF" }, // Indigo with White text
];

export const hash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};

export const placeholder = (name) => {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const idx = hash(name) % colors.length;
  const { bg, text } = colors[idx];
  return `data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22800%22%20height%3D%22800%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23${bg}%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Poppins,sans-serif%22%20font-size%3D%22120%22%20font-weight%3D%22bold%22%20fill%3D%22%23${text}%22%20text-anchor%3D%22middle%22%20dy%3D%22.3em%22%3E${initials}%3C%2Ftext%3E%3C%2Fsvg%3E`;
};

// Verified-loading visuals (confirmed in earlier screenshots of this project).
export const I = {
  branding1: UNSPLASH("photo-1713873010288-c4617fc2b15b"),      // branding mockup sheets
  branding2: UNSPLASH("photo-1762525984874-83d6ddf6a069"),      // identity system
  giftbox1:  UNSPLASH("photo-1694481901573-a970f982ac5e"),      // luxury gift box
  giftbox2:  UNSPLASH("photo-1668127494486-f27a1d2b88f9"),      // gift
  event1:    UNSPLASH("photo-1768508947591-b650dacc70a4"),      // event/exhibition
  press1:    PEXELS(6620970),                                   // printing press
  banner1:   PEXELS(6620972),                                   // banners
  stationery: PEXELS(5706026),                                  // stationery system
  exhibition: PEXELS(35042459),                                 // exhibition
  gala:       PEXELS(16120239),                                 // gala / luxury event
  corp:       PEXELS(7108269),                                  // corp team
  architecture: PEXELS(13219418),                               // modern architecture
};

// Map each category bucket -> array of curated images (rotated per product).
export const POOL = {
  signage:    [I.architecture, I.branding2, I.exhibition],
  display:    [I.exhibition, I.event1, I.gala],
  brochure:   [I.stationery, I.branding1, I.press1],
  banner:     [I.banner1, I.event1, I.exhibition],
  acrylic:    [I.branding1, I.stationery, I.giftbox2],
  mug:        [I.giftbox1, I.giftbox2, I.stationery],
  badge:      [I.stationery, I.branding2, I.giftbox2],
  pouch:      [I.giftbox1, I.giftbox2, I.stationery],
  lightbox:   [I.architecture, I.exhibition, I.event1],
  packaging:  [I.giftbox1, I.giftbox2, I.banner1],
  print:      [I.press1, I.branding1, I.stationery],
  promo:      [I.giftbox1, I.giftbox2, I.event1],
  flag:       [I.banner1, I.exhibition, I.event1],
  sheet:      [I.press1, I.stationery, I.branding1],
  podium:     [I.event1, I.exhibition, I.gala],
};

// Pick a category bucket based on keywords in product name.
export function bucketFor(name = "") {
  const n = name.toLowerCase();
  if (/(sign|board|door|airport|locator|info line)/.test(n)) return "signage";
  if (/(podium)/.test(n)) return "podium";
  if (/(brochure|holder|netted|zigzag)/.test(n)) return "brochure";
  if (/(banner|roll up|flying|square)/.test(n)) return "banner";
  if (/(flag|q pole|pole)/.test(n)) return "flag";
  if (/(acrylic|hinges)/.test(n)) return "acrylic";
  if (/(mug)/.test(n)) return "mug";
  if (/(badge|reel|lanyard|wrist)/.test(n)) return "badge";
  if (/(pouch|soft)/.test(n)) return "pouch";
  if (/(light box|lightbox|snap frame|rotating)/.test(n)) return "lightbox";
  if (/(box|raffle|drum|packaging)/.test(n)) return "packaging";
  if (/(sheet|tape|film|foam|micron|paper)/.test(n)) return "sheet";
  if (/(display|stand|easel|glorifier|organizer|table|wall)/.test(n)) return "display";
  if (/(print|press|offset|digital)/.test(n)) return "print";
  if (/(velcro|tissue|clip)/.test(n)) return "promo";
  return "promo";
}

// Deterministic primary image — same product always gets the same image.
export function hashIndex(slug, mod) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return h % mod;
}
